import { useEffect } from "react";

interface PageMetaOptions {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  robots?: string;
  locale?: string;
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const BASE_URL = "https://lesteroliver.com";

function setOrCreate(selector: string, attr: string, attrValue: string, content: string) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = "https://lesteroliver.com/og-image.png",
  robots,
  locale = "en_US",
  articleMeta,
}: PageMetaOptions) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", description);

    // Canonical
    const canonicalUrl = canonical || `${BASE_URL}${window.location.pathname}`;
    let linkCanon = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanon) {
      linkCanon = document.createElement("link");
      linkCanon.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanon);
    }
    linkCanon.setAttribute("href", canonicalUrl);

    // Robots meta
    let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (robots) {
      if (!metaRobots) {
        metaRobots = document.createElement("meta");
        metaRobots.setAttribute("name", "robots");
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute("content", robots);
    } else if (metaRobots) {
      metaRobots.remove();
    }

    // OG tags
    const ogTags: Record<string, string> = {
      "og:title": title,
      "og:description": description,
      "og:url": canonicalUrl,
      "og:type": ogType,
      "og:locale": locale,
      "og:site_name": "Lester Oliver",
    };
    if (ogImage) ogTags["og:image"] = ogImage;

    Object.entries(ogTags).forEach(([property, content]) => {
      setOrCreate(`meta[property="${property}"]`, "property", property, content);
    });

    // Twitter tags
    setOrCreate('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setOrCreate('meta[name="twitter:title"]', "name", "twitter:title", title);
    setOrCreate('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (ogImage) setOrCreate('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    // Article meta (for blog posts)
    if (articleMeta) {
      if (articleMeta.publishedTime) setOrCreate('meta[property="article:published_time"]', "property", "article:published_time", articleMeta.publishedTime);
      if (articleMeta.modifiedTime) setOrCreate('meta[property="article:modified_time"]', "property", "article:modified_time", articleMeta.modifiedTime);
      if (articleMeta.author) setOrCreate('meta[property="article:author"]', "property", "article:author", articleMeta.author);
      if (articleMeta.section) setOrCreate('meta[property="article:section"]', "property", "article:section", articleMeta.section);
      articleMeta.tags?.forEach((tag, i) => {
        setOrCreate(`meta[property="article:tag"][data-idx="${i}"]`, "property", "article:tag", tag);
      });
    }

    return () => {
      document.title = "Lester Oliver | Solutions Consultant & POC Builder | Virginia, USA";
      if (metaDesc) metaDesc.setAttribute("content", "Lester Oliver is a solutions consultant and rapid POC builder in Virginia, USA. I turn ideas into working prototypes using AI-powered development, consulting, and project management.");
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
      // Clean up article meta
      document.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogType, ogImage, robots, locale, articleMeta]);
}
